import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, Warning, ArrowRight, ClockCountdown, XCircle, CheckCircle } from "@phosphor-icons/react";
import api from "../lib/api";
import { fmtEUR } from "../lib/format";

const SYMBOL_COLORS = {
  BTC: "#F7931A", ETH: "#627EEA", USDT: "#26A17B", USDC: "#2775CA",
  XRP: "#111827", DOGE: "#C2A633", ADA: "#0033AD", MATIC: "#8247E5",
};

const STATE_LABELS = {
  awaiting_payment: "Awaiting payment",
  awaiting_confirmation: "Awaiting confirmation",
  credited: "Credited",
  rejected: "Rejected",
  cancelled: "Cancelled",
  expired: "Expired",
};

const STATE_STYLES = {
  awaiting_payment: "bg-amber-50 text-amber-700",
  awaiting_confirmation: "bg-cobalt-50 text-cobalt-600",
  credited: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-600",
  cancelled: "bg-ink-100 text-ink-500",
  expired: "bg-ink-100 text-ink-500",
};

export default function CryptoFunding({ amountEur, accepted, onError }) {
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [symbol, setSymbol] = useState(null);
  const [assetId, setAssetId] = useState(null);
  const [deposit, setDeposit] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [txHash, setTxHash] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadDeposits = () =>
    api.get("/me/wallet/crypto-deposits").then(({ data }) => setDeposits(data)).catch(() => {});

  useEffect(() => {
    api.get("/crypto/assets").then(({ data }) => setAssets(data)).catch(() => {});
    loadDeposits();
  }, []);

  const bySymbol = useMemo(() => {
    const m = {};
    assets.forEach((a) => { (m[a.symbol] = m[a.symbol] || []).push(a); });
    return m;
  }, [assets]);

  const networks = symbol ? bySymbol[symbol] || [] : [];
  const selected = assets.find((a) => a.id === assetId) || null;

  const pickSymbol = (s) => {
    setSymbol(s);
    setDeposit(null);
    const opts = bySymbol[s] || [];
    setAssetId(opts.length === 1 ? opts[0].id : null);
  };

  const createDeposit = async () => {
    if (!accepted) {
      onError?.(t("checkout.termsRequired", "Please accept the Terms & Conditions and Privacy Policy to continue."));
      return;
    }
    onError?.("");
    setLoading(true);
    try {
      const { data } = await api.post("/me/wallet/crypto-deposits", {
        asset_id: assetId, amount_eur: amountEur,
      });
      setDeposit(data);
      setTxHash("");
      loadDeposits();
    } catch (e) {
      onError?.(e.response?.data?.detail || t("common.error", "Something went wrong. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const submitTx = async () => {
    if (!txHash.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/me/wallet/crypto-deposits/${deposit.id}/tx`, { tx_hash: txHash.trim() });
      setDeposit({ ...deposit, state: "awaiting_confirmation", tx_hash: txHash.trim() });
      loadDeposits();
    } catch (e) {
      onError?.(e.response?.data?.detail || t("common.error", "Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(selected.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="mt-5" data-testid="crypto-funding">
      {!deposit && (
        <>
          <div className="text-xs text-ink-500 uppercase tracking-widest font-semibold mb-2">
            {t("crypto.selectAsset", "Select cryptocurrency")}
          </div>
          <div className="grid grid-cols-4 gap-2" data-testid="crypto-asset-grid">
            {Object.keys(bySymbol).map((s) => (
              <button
                key={s}
                onClick={() => pickSymbol(s)}
                className={`py-2.5 rounded-2xl font-semibold text-sm border flex flex-col items-center gap-1 ${symbol === s ? "border-cobalt-500 bg-cobalt-50" : "border-ink-200 bg-white hover:border-cobalt-300"}`}
                data-testid={`crypto-asset-${s.toLowerCase()}`}
              >
                <span className="w-6 h-6 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
                  style={{ backgroundColor: SYMBOL_COLORS[s] || "#334155" }}>
                  {s.slice(0, 1)}
                </span>
                {s}
              </button>
            ))}
          </div>

          {symbol && networks.length > 1 && (
            <div className="mt-4">
              <div className="text-xs text-ink-500 uppercase tracking-widest font-semibold mb-2">
                {t("crypto.selectNetwork", "Select network")} <span className="text-coral-deep">*</span>
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="crypto-network-grid">
                {networks.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => setAssetId(n.id)}
                    className={`py-3 px-3 rounded-2xl font-semibold text-sm border text-left ${assetId === n.id ? "border-cobalt-500 bg-cobalt-50" : "border-ink-200 bg-white hover:border-cobalt-300"}`}
                    data-testid={`crypto-network-${n.network_code.toLowerCase()}`}
                  >
                    {n.network}
                    <div className="text-[10px] font-normal text-ink-500 mt-0.5">{n.symbol} · {n.network_code}</div>
                  </button>
                ))}
              </div>
              <div className="mt-2 flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 rounded-xl p-2.5">
                <Warning size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
                {t("crypto.networkCritical", "Network selection is critical. Sending on the wrong network can permanently lose your funds.")}
              </div>
            </div>
          )}

          {selected && (
            <div className="mt-4 text-sm bg-white rounded-2xl border border-ink-200 p-4" data-testid="crypto-summary">
              <div className="flex justify-between"><span className="text-ink-500">{t("crypto.funding", "Funding amount")}</span><span className="font-semibold">{fmtEUR(amountEur)}</span></div>
              <div className="flex justify-between mt-1"><span className="text-ink-500">{t("crypto.asset", "Asset · Network")}</span><span className="font-semibold">{selected.symbol} · {selected.network}</span></div>
              {selected.rate_eur && (
                <div className="flex justify-between mt-1">
                  <span className="text-ink-500">{t("crypto.estimated", "Estimated amount")}</span>
                  <span className="font-semibold">≈ {(amountEur / selected.rate_eur).toFixed(selected.decimals)} {selected.symbol}</span>
                </div>
              )}
            </div>
          )}

          <button
            onClick={createDeposit}
            disabled={!assetId || loading || amountEur < 5}
            className="btn-primary w-full mt-4 text-base disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="crypto-create-deposit"
          >
            {loading ? t("checkout.processing", "Processing…") : <>{t("crypto.showAddress", "Show deposit address")} <ArrowRight size={16} weight="bold" /></>}
          </button>
        </>
      )}

      {deposit && selected && (
        <div data-testid="crypto-deposit-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-lg">{deposit.symbol} · {deposit.network}</div>
            <span className={`pill text-xs px-2.5 py-1 rounded-full font-semibold ${STATE_STYLES[deposit.state]}`} data-testid="crypto-deposit-state">
              {STATE_LABELS[deposit.state] || deposit.state}
            </span>
          </div>

          <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4" data-testid="crypto-network-warning">
            <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5 text-amber-600" />
            <span className="font-medium">{deposit.warning || `Only send ${deposit.symbol} using the ${deposit.network} network to this address. Funds sent using an unsupported network may be permanently lost.`}</span>
          </div>

          <div className="bg-white rounded-2xl border border-ink-200 p-4 flex flex-col items-center">
            <QRCodeSVG value={deposit.address} size={148} data-testid="crypto-qr" />
            <div className="mt-3 w-full">
              <div className="text-[10px] text-ink-500 uppercase tracking-widest font-semibold">{t("crypto.address", "Receiving address")}</div>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 text-[11px] break-all bg-sky-soft/50 rounded-lg p-2" data-testid="crypto-address">{deposit.address}</code>
                <button onClick={copyAddress} className="p-2 rounded-lg border border-ink-200 hover:border-cobalt-300 flex-shrink-0" data-testid="crypto-copy-address" title="Copy address">
                  {copied ? <Check size={16} className="text-emerald-600" weight="bold" /> : <Copy size={16} />}
                </button>
              </div>
            </div>
            <div className="mt-3 w-full grid grid-cols-2 gap-2 text-xs">
              <div className="bg-sky-soft/50 rounded-lg p-2">
                <div className="text-ink-500">{t("crypto.send", "Send exactly")}</div>
                <div className="font-bold">{deposit.expected_crypto_amount} {deposit.symbol}</div>
                <div className="text-ink-500">≈ {fmtEUR(deposit.amount_eur)}</div>
              </div>
              <div className="bg-sky-soft/50 rounded-lg p-2">
                <div className="text-ink-500">{t("crypto.reference", "Deposit reference")}</div>
                <div className="font-bold" data-testid="crypto-deposit-reference">{deposit.reference}</div>
                <div className="text-ink-500">{deposit.network_code}</div>
              </div>
            </div>
          </div>

          {deposit.state === "awaiting_payment" && (
            <div className="mt-4">
              <div className="text-xs text-ink-500 uppercase tracking-widest font-semibold mb-1">
                {t("crypto.txHash", "Transaction hash (after you send)")}
              </div>
              <div className="flex gap-2">
                <input
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  placeholder="0x… / txid"
                  className="flex-1 input-soft text-sm"
                  data-testid="crypto-tx-input"
                />
                <button onClick={submitTx} disabled={!txHash.trim() || submitting}
                  className="btn-primary px-4 text-sm disabled:opacity-50" data-testid="crypto-tx-submit">
                  {submitting ? "…" : t("crypto.submit", "Submit")}
                </button>
              </div>
              <p className="mt-2 text-[11px] text-ink-500">
                {t("crypto.creditNote", "Your balance is credited only after the transaction is verified on the blockchain by our team — typically within a few hours.")}
              </p>
            </div>
          )}

          {deposit.state === "awaiting_confirmation" && (
            <div className="mt-4 flex items-start gap-2 text-xs text-cobalt-700 bg-cobalt-50 rounded-xl p-3" data-testid="crypto-awaiting-note">
              <ClockCountdown size={16} className="flex-shrink-0 mt-0.5" />
              {t("crypto.awaitingNote", "Transaction received. We're verifying it on-chain — your wallet will be credited once confirmed. You can safely leave this page.")}
            </div>
          )}

          <button onClick={() => { setDeposit(null); loadDeposits(); }} className="mt-4 text-xs text-cobalt-600 font-semibold hover:underline" data-testid="crypto-new-deposit">
            ← {t("crypto.back", "Start another deposit")}
          </button>
        </div>
      )}

      {deposits.length > 0 && (
        <div className="mt-6">
          <div className="text-xs text-ink-500 uppercase tracking-widest font-semibold mb-2">{t("crypto.history", "Your crypto deposits")}</div>
          <div className="space-y-2" data-testid="crypto-deposit-list">
            {deposits.slice(0, 5).map((d) => (
              <div key={d.id} className="flex items-center justify-between text-xs bg-white rounded-xl border border-ink-100 px-3 py-2">
                <div>
                  <span className="font-semibold">{d.symbol}</span> <span className="text-ink-500">· {d.network} · {d.reference}</span>
                  <div className="text-ink-500">{fmtEUR(d.amount_eur)}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${STATE_STYLES[d.state]}`}>
                  {d.state === "credited" ? <CheckCircle size={12} weight="fill" /> : d.state === "rejected" ? <XCircle size={12} weight="fill" /> : <ClockCountdown size={12} />}
                  {STATE_LABELS[d.state] || d.state}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
